import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiArrowLeft, FiShoppingCart, FiDollarSign, FiTrendingUp } from 'react-icons/fi';

const TeamMemberDetail = () => {
  const { id } = useParams();
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMemberDetail = async () => {
      try {
        const response = await api.get(`/team/${id}`);
        setMemberData(response.data);
      } catch (error) {
        toast.error("Impossible de charger les détails de l'employé.");
      } finally {
        setLoading(false);
      }
    };

    fetchMemberDetail();
  }, [id]);

  if (loading) {
    return <Spinner message="Chargement des détails de l'employé..." />;
  }

  if (!memberData) {
    return <div className="text-center text-red-500">Données de l'employé non trouvées.</div>;
  }

  const { user, stats, recentSales } = memberData;

  return (
    <div>
      <Link to="/dashboard/team" className="inline-flex items-center text-brand-blue hover:underline mb-6">
        <FiArrowLeft className="mr-2" />
        Retour à la gestion d'équipe
      </Link>
      
      {/* Header */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6">
        <div className="flex items-center">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
            <FiUser className="w-8 h-8 text-gray-500" />
          </div>
          <div>
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-bold text-gray-800">{user.username}</h2>
              <span className={`capitalize text-xs font-semibold px-2 py-1 rounded-full ${ user.role === 'owner' ? 'bg-green-200 text-green-900' : user.role === 'manager' ? 'bg-purple-200 text-purple-900' : 'bg-blue-200 text-blue-900' }`}>
                {user.role === 'owner' ? 'Propriétaire' : user.role}
              </span>
            </div>
            <p className="text-gray-500 flex items-center mt-1"><FiMail className="mr-2" size={14}/> {user.email}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-blue-50 p-6 rounded-2xl flex items-center gap-4 border border-blue-100">
          <div className="bg-blue-200 p-3 rounded-full"><FiShoppingCart className="h-6 w-6 text-blue-800" /></div>
          <div>
            <p className="text-sm text-blue-700">Ventes réalisées</p>
            <p className="font-bold text-2xl text-blue-900">{stats.totalSalesCount || 0}</p>
          </div>
        </div>
        <div className="bg-green-50 p-6 rounded-2xl flex items-center gap-4 border border-green-100">
          <div className="bg-green-200 p-3 rounded-full"><FiTrendingUp className="h-6 w-6 text-green-800" /></div>
          <div>
            <p className="text-sm text-green-700">Chiffre d'affaires généré</p>
            <p className="font-bold text-2xl text-green-900">{(stats.totalSalesValue || 0).toLocaleString('fr-FR')} CFA</p>
          </div>
        </div>
      </div>

      {/* Recent Sales */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center"><FiDollarSign className="mr-2"/>Ventes Récentes</h3>
        <div className="max-h-96 overflow-y-auto">
          {recentSales?.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="py-3 px-4">Date</th><th className="py-3 px-4">Articles</th><th className="py-3 px-4 text-right">Total</th></tr></thead>
              <tbody>
                {recentSales.map(sale => (
                  <tr key={sale._id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{new Date(sale.date).toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="py-3 px-4">{sale.items.length}</td>
                    <td className="py-3 px-4 text-right font-semibold text-brand-blue">{sale.totalAmount.toLocaleString('fr-FR')} CFA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-center text-gray-500 py-8">Aucune vente enregistrée pour cet employé.</p>}
        </div>
      </div>
    </div>
  );
};

export default TeamMemberDetail;